'use client';

import { BillingCycle } from '@prezly/sdk';
import classNames from 'classnames';
import { usePlausible } from 'next-plausible';
import { usePostHog } from 'posthog-js/react';

import { Link } from '@/components/Link/Link';
import { ModalName } from '@/constants';
import { CTA } from '@/lib/tracking';
import { segment } from '@/lib/tracking/segmentAnalytics';
import { usePricingPageContext } from '@/pricing/PricingPageContext';
import * as Pricing from '@/pricing/types';
import { Chip } from '@/src/ui/Chip/Chip';
import type { DataAttributes } from '@/types';
import { Badge } from '@/ui/Badge/Badge';
import { ButtonLink } from '@/ui/Button/ButtonLink';
import { getPrice } from '@/util/getPrice';
import { slugify } from '@/util/strings/slugify';

import { FeaturesList } from './FeaturesList';

import styles from './PricingCard.module.scss';

interface Props extends DataAttributes {
    action: 'start-a-trial' | 'contact-support';
    displayAsPackage: boolean;
    isAgencyPlan: boolean;
    option: Pricing.Option;
    withSeeAllFeaturesLink: boolean;
}

const SIGN_UP_URL = 'https://rock.prezly.com/signup';

function PricingCard({
    action,
    option,
    withSeeAllFeaturesLink,
    displayAsPackage,
    ...attributes
}: Props) {
    const posthog = usePostHog();

    const plausible = usePlausible();
    const { prices, display_name, overview } = option;

    const { currency, billingCycle } = usePricingPageContext();

    const isPopular = option.id === 'standard' || option.id === 'agency_standard';
    const isEnterprisePlan = option.id === Pricing.OptionId.ENTERPRISE;

    const selectedPrice =
        isEnterprisePlan || !prices ? 'Custom' : getPrice(prices, billingCycle, currency);

    const isYearlyCycle = billingCycle === BillingCycle.YEAR;

    let discountIndicator = '20% off';
    switch (display_name) {
        case 'Agency Starter':
            discountIndicator = '50% off';
            break;
        case 'Agency Small':
        case 'Agency Medium':
            discountIndicator = '30% off';
            break;
    }

    function handleStartTrialClick() {
        posthog.capture(CTA.CLICKED, { href: `${SIGN_UP_URL}?plan=${option.id}` });
        segment.track(CTA.CLICKED, { href: `${SIGN_UP_URL}?plan=${option.id}` });
        plausible('CTAs', {
            props: {
                href: `${SIGN_UP_URL}?plan=${option.id}`,
                label: 'Start your free trial',
            },
        });
    }

    function handleContactSalesClick() {
        posthog.capture(CTA.CLICKED, { href: ModalName.TALK_TO_SALES });
        segment.track(CTA.CLICKED, { href: ModalName.TALK_TO_SALES });
        plausible('CTAs', {
            props: {
                href: ModalName.TALK_TO_SALES,
                label: 'Contact sales',
            },
        });
    }

    return (
        <div
            data-pricing-card={option.id}
            {...attributes}
            className={classNames(styles.card, {
                [styles.withShadow]: isPopular,
            })}
        >
            <div>
                <h3 className={styles.title} data-pricing-card-title={slugify(display_name)}>
                    {display_name}
                    {isPopular && <Chip variant="primary">Popular</Chip>}
                </h3>

                <div className={styles.intro} data-pricing-card-intro>
                    <h4 className={styles.price}>
                        <span className={styles.value} data-pricing-card-price>
                            {selectedPrice}
                        </span>
                        {isYearlyCycle && !isEnterprisePlan && (
                            <Badge
                                data-pricing-card-badge={slugify(discountIndicator)}
                                variant="white"
                            >
                                {discountIndicator}
                            </Badge>
                        )}
                    </h4>

                    <div className={styles.billingCycle} data-pricing-card-billing-cycle>
                        {buildBillingString(billingCycle, displayAsPackage)}
                    </div>
                </div>

                <FeaturesList
                    data-pricing-card-feature-list
                    className={styles.featuresList}
                    overview={overview}
                />
            </div>

            <div className={styles.actions}>
                {action === 'start-a-trial' && (
                    <ButtonLink
                        data-pricing-card-action={action}
                        href={`${SIGN_UP_URL}?plan=${option.id}`}
                        size="large"
                        className={styles.choosePlanButton}
                        variant={isPopular ? 'primary' : 'secondary'}
                        onClick={handleStartTrialClick}
                    >
                        Start your free trial
                    </ButtonLink>
                )}
                {action === 'contact-support' && (
                    <ButtonLink
                        data-pricing-card-action={action}
                        href={`#${ModalName.TALK_TO_SALES}`}
                        size="large"
                        className={styles.choosePlanButton}
                        variant={isPopular ? 'primary' : 'secondary'}
                        onClick={handleContactSalesClick}
                    >
                        Contact sales
                    </ButtonLink>
                )}
                {withSeeAllFeaturesLink && (
                    <Link
                        data-pricing-card-action="see-all-features"
                        className={styles.seeAll}
                        href="#pricing-table"
                    >
                        See all features
                    </Link>
                )}
            </div>
        </div>
    );
}

function buildBillingString(billingCycle: BillingCycle, showAsPackage: boolean): string {
    const isMonthlyCycle = billingCycle === BillingCycle.MONTH;

    if (showAsPackage) {
        return isMonthlyCycle ? 'Per month, billed monthly' : 'Per month, billed annually';
    }

    return isMonthlyCycle ? 'Per user/month, billed monthly' : 'Per user/month, billed annually';
}

export default PricingCard;
